/**
 * DÜV Control Events - Betrieb Müller (v2)
 *
 * Events related to DÜV (Düngeverordnung) control readiness.
 */

import type { MockControlEvent, InspectionLikelihood } from '@hofsync/design-system';
import { hoursAgo, daysFromNow, monthsFromNow } from '../farm-profile';
import { DUEV_10_2, STOFFBILV_6 } from '../../legal';

/**
 * DÜV inspection likelihood assessment
 */
const DUEV_LIKELIHOOD: InspectionLikelihood = {
  level: 'mittel',
  factors: [
    { label: 'Letzte Kontrolle', value: 'März 2023 (vor 34 Monaten)' },
    { label: 'Kontrollzyklus Region', value: 'Ø 3 Jahre (36 Monate)' },
    { label: 'Betriebsgröße', value: '85 ha (>15 ha)' },
    { label: 'Viehbesatz', value: '160 GV (bilanzierungspflichtig)' },
    { label: 'Rotes Gebiet', value: 'Nein' },
  ],
  disclaimer:
    'Die Kontrollwahrscheinlichkeit basiert auf Erfahrungswerten und historischen Daten. ' +
    'Es handelt sich um eine Einschätzung, nicht um eine Vorhersage.',
  period: 'Q1-Q2 2026',
};

/**
 * DÜV-Kontrolle Vorbereitung (WARNING)
 *
 * Required documents for DÜV control (10 total):
 * 1. Anbauplan / Ackerschlagkartei - basis for fertilizer planning
 * 2. Düngebedarfsermittlung je Schlag - fertilizer needs per field
 * 3. Düngedokumentation / Schlagkartei - application records
 * 4. Nährstoffbilanz / Stoffstrombilanz - nutrient balance
 * 5. Lieferscheine Wirtschaftsdünger - organic fertilizer delivery notes
 * 6. Nährstoffanalyse Wirtschaftsdünger - nutrient analysis of organic fertilizers
 * 7. Wirtschaftsdünger-Portal Meldungen - portal registrations
 * 8. Bodenuntersuchungen - soil sample reports
 * 9. Tierbestandsregister (HIT) - animal inventory
 * 10. Lagerkapazität Nachweise - storage capacity documentation
 */
export const EVENT_DUEV_PREP: MockControlEvent = {
  id: 'evt-duev-prep',
  type: 'attention-required',
  priority: 'warning',
  title: 'DÜV-Kontrolle: 7 von 10 Dokumenten vorhanden',
  subtitle: 'Kontrollbereitschaft',
  timestamp: hoursAgo(4),
  relevantDate: daysFromNow(14), // Inspection likelihood Q1-Q2 2026 → NEXT MONTH
  relatedEntity: { name: 'DÜV-Kontrollbereitschaft', href: '#duev' },
  categories: ['duev'],
  controlType: 'DÜV-Kontrolle',
  controlReadiness: {
    status: 'warning',
    metCount: 7,
    totalCount: 10,
    label: '7 von 10 vorhanden',
  },
  inspectionLikelihood: DUEV_LIKELIHOOD,

  // === AFFECTED OBJECTS (sorted by severity) ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    // CRITICAL - Overdue
    {
      id: 'doc-bodenprobe-grosses-feld',
      name: 'Bodenprobe Großes Feld',
      type: 'document',
      severity: 'critical',
      status: 'Überfällig seit 10 Monaten',
      action: { label: 'Probe beauftragen', href: '/my-farm/fields/grosses-feld' },
    },
    // WARNING - Missing but not overdue
    {
      id: 'doc-stoffstrombilanz-2025',
      name: 'Stoffstrombilanz 2025',
      type: 'document',
      severity: 'warning',
      status: 'Noch nicht erstellt (Frist 31.03.2026)',
      action: { label: 'Jetzt erstellen', href: '/my-farm/documents/stoffstrombilanz' },
    },
    {
      id: 'doc-lagerkapazitaet',
      name: 'Lagerkapazität Nachweise',
      type: 'document',
      severity: 'warning',
      status: 'Fehlt - Berechnung erforderlich',
      action: { label: 'Berechnung erstellen', href: '/my-farm/storage/capacity' },
    },
    // INFO - Present documents
    {
      id: 'doc-anbauplan-2025',
      name: 'Anbauplan / Schlagkartei 2025',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (aus topfarmplan)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/anbauplan' },
    },
    {
      id: 'doc-dbe-2025',
      name: 'Düngebedarfsermittlung 2025',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (21 Schläge)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/dbe' },
    },
    {
      id: 'doc-duengedoku-2025',
      name: 'Düngedokumentation 2025',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (Schlagkartei)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/duengedoku' },
    },
    {
      id: 'doc-lieferscheine-wd',
      name: 'Lieferscheine Wirtschaftsdünger',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (12 Belege)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/lieferscheine' },
    },
    {
      id: 'doc-naehrstoffanalyse-wd',
      name: 'Nährstoffanalyse Wirtschaftsdünger',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (Gülle, Mist)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/naehrstoffanalyse' },
    },
    {
      id: 'doc-wd-portal',
      name: 'Wirtschaftsdünger-Portal Meldungen',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (alle Meldungen)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/wd-portal' },
    },
    {
      id: 'doc-tierbestand-hit',
      name: 'Tierbestandsregister (HIT)',
      type: 'document',
      severity: 'info',
      status: 'Vorhanden (160 GV)',
      action: { label: 'Anzeigen', href: '/my-farm/documents/hit' },
    },
  ],
  domainLink: {
    label: 'Alle DÜV-Dokumente anzeigen',
    href: '/my-farm/compliance/duev',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'DÜV-Kontrolle wahrscheinlich Q1-Q2 2026. 3 Dokumente fehlen noch.',
    legalBasis: [DUEV_10_2],
    actionRecommendation:
      'Fehlende Dokumente der Reihe nach abarbeiten: Zuerst die überfällige Bodenprobe beauftragen, ' +
      'dann Stoffstrombilanz und Lagerkapazitätsnachweis erstellen.',
    factors: [
      { label: 'Dokumente vorhanden', value: '7 von 10' },
      { label: 'Davon kritisch', value: '1 überfällig' },
      { label: 'Letzte Kontrolle', value: 'März 2023' },
      { label: 'Wahrscheinlichkeit', value: 'Mittel (Q1-Q2)' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 7 (THIS SEASON - Shown): Nährstoffvergleich Frist (WARNING)
 */
export const EVENT_NAEHRSTOFFVERGLEICH: MockControlEvent = {
  id: 'evt-naehrstoffvergleich',
  type: 'obligation-due',
  priority: 'warning',
  title: 'Nährstoffvergleich 2025: Frist 31.03.2026',
  subtitle: 'Aufzeichnungspflicht',
  timestamp: hoursAgo(48),
  relevantDate: new Date('2026-03-31'), // 64 days
  relatedEntity: { name: 'Nährstoffvergleich', href: '#naehrstoffvergleich' },
  categories: ['duev', 'fristen'],
  controlType: 'Aufzeichnung',

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'doc-nv-2025',
      name: 'Nährstoffvergleich 2025',
      type: 'document',
      severity: 'warning',
      status: 'Noch nicht erstellt',
      action: { label: 'Jetzt erstellen', href: '/my-farm/documents/naehrstoffvergleich' },
    },
  ],
  domainLink: {
    label: 'Düngedokumentation anzeigen',
    href: '/my-farm/fertilization',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Jährlicher Nährstoffvergleich muss bis 31.03.2026 erstellt werden.',
    legalBasis: [DUEV_10_2],
    factors: [
      { label: 'Düngejahr', value: '2025' },
      { label: 'Frist', value: '31.03.2026' },
      { label: 'Verbleibend', value: '64 Tage' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * Event 8 (THIS SEASON - Shown): Stoffstrombilanz Frist (WARNING)
 */
export const EVENT_STOFFSTROM_FRIST: MockControlEvent = {
  id: 'evt-stoffstrom-frist',
  type: 'obligation-due',
  priority: 'warning',
  title: 'Stoffstrombilanz 2025: Frist 31.03.2026',
  subtitle: 'Bilanzierungspflicht',
  timestamp: hoursAgo(72),
  relevantDate: new Date('2026-03-31'),
  relatedEntity: { name: 'Stoffstrombilanz', href: '#stoffstrom' },
  categories: ['duev', 'fristen'],
  controlType: 'Stoffstrombilanz',

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'doc-ssb-2025',
      name: 'Stoffstrombilanz 2025',
      type: 'document',
      severity: 'warning',
      status: 'Noch nicht erstellt',
      action: { label: 'Jetzt erstellen', href: '/my-farm/documents/stoffstrombilanz' },
    },
  ],
  domainLink: {
    label: 'Nährstoffbilanzen anzeigen',
    href: '/my-farm/balances',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Stoffstrombilanz für bilanzierungspflichtige Betriebe bis 31.03.2026.',
    legalBasis: [STOFFBILV_6],
    factors: [
      { label: 'Bilanzjahr', value: '2025' },
      { label: 'Frist', value: '31.03.2026' },
      { label: 'Grund', value: '160 GV (>50 GV)' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * REMOVED: EVENT_DUEV_PARTIAL_READY
 * Was duplicate of EVENT_DUEV_PREP (same 6/8 status).
 * Per aggregation rule: one event per obligation status.
 */

/**
 * Event: Düngeplanung 2026 (INFO)
 */
export const EVENT_DUENGEPLANUNG_2026: MockControlEvent = {
  id: 'evt-duengeplanung-2026',
  type: 'situation-updated',
  priority: 'info',
  title: 'Düngeplanung 2026: Jetzt vorbereiten',
  subtitle: 'Planung',
  timestamp: hoursAgo(120),
  relevantDate: monthsFromNow(2), // Before spring application
  relatedEntity: { name: 'Düngebedarfsermittlung', href: '#dbe' },
  categories: ['duev'],
  controlType: 'Düngebedarfsermittlung',

  // === TWO-COLUMN LAYOUT ===
  isAggregate: true,
  hideAggregateBadge: true,
  affectedEntities: [
    {
      id: 'doc-dbe-2026',
      name: 'Düngebedarfsermittlung 2026',
      type: 'document',
      severity: 'info',
      status: 'Noch nicht erstellt',
      action: { label: 'Jetzt erstellen', href: '/my-farm/documents/dbe' },
    },
  ],
  domainLink: {
    label: 'Düngeplanung anzeigen',
    href: '/my-farm/fertilization/planning',
  },

  // === WHY THIS MATTERS ===
  reasoning: {
    summary: 'Düngebedarfsermittlung vor Vegetationsbeginn erstellen.',
    legalBasis: [],
    factors: [
      { label: 'Empfehlung', value: 'Vor erster Düngung' },
      { label: 'Vegetationsbeginn', value: 'ca. März 2026' },
    ],
    confidence: 'high',
  },
  entities: [],
};

/**
 * All DÜV control events
 *
 * Note: EVENT_DUEV_PARTIAL_READY removed - was duplicate of EVENT_DUEV_PREP
 */
export const DUEV_CONTROL_EVENTS: MockControlEvent[] = [
  EVENT_DUEV_PREP,
  EVENT_NAEHRSTOFFVERGLEICH,
  EVENT_STOFFSTROM_FRIST,
  EVENT_DUENGEPLANUNG_2026,
];
